// Build Line - the enrollment wizard.
//
// Four steps, and the order they run in is the whole design (spec 8):
//
//   1. Pick the profile. Its notes are the nuance an operator has to remember.
//   2. Roster + the credentials this order borrows + the photos.
//   3. Review the snapshot the coordinator will read for the life of the build.
//   4. Dry-run preflight, then launch.
//
// WHAT THIS SCREEN IS RESPONSIBLE FOR. The snapshot written at step 3 is the
// ONLY thing every station downstream reads (design 4.2). A block written wrong
// here is not a bad request - it is a parked order hours later, and two of those
// parks are PERMANENT codes that need an escape hatch to clear. The server
// refuses all of it too, before a row exists; this form's job is to make the
// refusals impossible to earn in the first place, and to say why in the field
// that caused them.
//
// THE PHOTO EXPANSION IS THIS FILE'S ONE PIECE OF REAL LOGIC. Photos are
// supplied per PERSONA - one face per identity, reused across every domain that
// identity sends from - but `overrides.photos.images` is keyed by FINAL ADDRESS,
// because the station does no expansion and no persona matching: it reads the
// map it is given. So the expansion happens here, before submit, or the photos
// are addressed to nobody.
//
// SECURITY: every key field below is write-only. It is typed in, it goes up in
// the enrollment (or in the one-shot probe), and nothing ever reads one back -
// the order rows carry `has_*` booleans and nothing else.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { BuildShowToast } from './BuildLine';
import './ProfileScreens.css';
import { PanelSplitFields, PanelSplitSummary } from './PanelSplitFields';
import { panelSplitLabel, validSerkenPercent, type PanelSplit } from './panel-split';
import {
  MAX_PHOTO_BYTES,
  MI_ALLOWED_APP_ID,
  REGISTRARS,
  REGISTRAR_CREDS_FIELDS,
  SUBDOMAIN_TAG,
  buildApi,
  buildErrorMessage,
  buildIssues,
  hostsAtRegistrar,
  registrarDnsOf,
  registrarIsUsed,
  resolveTags,
  type BuildEnrollmentIssue,
  type BuildOrderDraft,
  type BuildOrderPayload,
  type BuildPhotoEntry,
  type BuildPreflightReport,
  type BuildProbeReport,
  type BuildProfile,
  type BuildRegistrar,
} from './buildApi';
import {
  MAX_USERS_PER_DOMAIN, domainsOf, overloadedDomains, parseRoster, personasOf, type Persona,
} from './roster';

/** A Smartlead user id is the workspace owner's positive integer. The server
 *  enforces the same rule (src/lib/smartlead.ts isSmartleadUserId); this copy
 *  only lets the form say so before the field is left behind. */
const SMARTLEAD_USER_ID = /^[1-9]\d*$/;

const STEPS = [
  { n: 1, label: 'Profile' },
  { n: 2, label: 'Roster and credentials' },
  { n: 3, label: 'Snapshot' },
  { n: 4, label: 'Preflight and launch' },
] as const;

/** One persona's face, as the operator supplied it. `error` is the client-side
 *  refusal (an oversized file), kept beside the entry so it renders on the row
 *  that caused it. */
interface PersonaPhoto {
  url?: string;
  b64?: string;
  mime?: string;
  filename?: string;
  bytes?: number;
  error?: string;
}

/**
 * Why a photo URL is not acceptable, or null.
 *
 * The server refuses a non-https URL outright (`validatePhotoImages`), so the
 * same rule runs at entry: an operator who pastes an http link should learn it
 * on the row that holds it, not from a 400 two steps later. One definition,
 * used by both the inline message and the step gate, so they can never
 * disagree about which URLs are acceptable.
 */
function photoUrlProblem(entry: PersonaPhoto | undefined): string | null {
  if (!entry || entry.b64) return null;
  const url = (entry.url ?? '').trim();
  if (url === '') return null;
  return /^https:\/\//i.test(url) ? null : 'a photo URL must be https';
}

/** base64 without the `data:` prefix, which is what the snapshot carries. */
function readFileAsBase64(file: File): Promise<{ b64: string; mime: string; bytes: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('the file could not be read'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve({
        b64: comma >= 0 ? result.slice(comma + 1) : result,
        mime: file.type || 'image/jpeg',
        bytes: file.size,
      });
    };
    reader.readAsDataURL(file);
  });
}

export function Enroll({
  showToast,
  onLaunched,
}: {
  showToast: BuildShowToast;
  onLaunched: (orderId: number) => void;
}): JSX.Element {
  const [profiles, setProfiles] = useState<BuildProfile[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [step, setStep] = useState<number>(1);
  const [profileId, setProfileId] = useState<number | null>(null);

  // Step 2
  const [label, setLabel] = useState('');
  const [rosterCsv, setRosterCsv] = useState('');
  const [panelSplit, setPanelSplit] = useState<PanelSplit>({ panel_bucket: 'serken', serken_percent: 50 });
  /** The registrar THIS order delegates at. Null means "the profile default";
   *  a pick is per order because a customer's domains arrive at a different
   *  registrar each time, and the snapshot follows the pick, not the profile. */
  const [registrarPick, setRegistrarPick] = useState<BuildRegistrar | null>(null);
  const [registrarCreds, setRegistrarCreds] = useState<Record<string, string>>({});
  const [sequencerKey, setSequencerKey] = useState('');
  const [miKey, setMiKey] = useState('');
  const [miWorkspaceText, setMiWorkspaceText] = useState('');
  /** The workspace NAME the probe resolved for the current key, so the operator
   *  confirms a customer, not a bare number. Cleared the moment the key text
   *  changes - a name that survived a key swap would vouch for the wrong key. */
  const [miWorkspaceName, setMiWorkspaceName] = useState<string | null>(null);
  const [forwardingUrl, setForwardingUrl] = useState('');
  const [photos, setPhotos] = useState<Record<string, PersonaPhoto>>({});
  // Provider-specific coordinates. Smartlead's user id and the plusvibe/bison
  // workspaces are READ off the key by Probe; the bison URL is the operator's
  // to supply. PlusVibe's partner API takes the same key, so nothing else is.
  const [slUserId, setSlUserId] = useState('');
  const [bisonBaseUrl, setBisonBaseUrl] = useState('');
  const [seqWorkspaceId, setSeqWorkspaceId] = useState('');
  const [seqWorkspaces, setSeqWorkspaces] = useState<{ id: string; name: string }[]>([]);
  const [probe, setProbe] = useState<BuildProbeReport | null>(null);
  const [probeBusy, setProbeBusy] = useState(false);
  const [probeError, setProbeError] = useState<string | null>(null);

  // Step 3
  const [settingsText, setSettingsText] = useState('{}');
  const [optionalTags, setOptionalTags] = useState<string[]>([]);
  const [freeTagDraft, setFreeTagDraft] = useState('');
  const [miEnabled, setMiEnabled] = useState(false);
  const [photosEnabled, setPhotosEnabled] = useState(true);
  const [trackingAttach, setTrackingAttach] = useState(true);

  // Steps 3 and 4
  const [draft, setDraft] = useState<BuildOrderDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftIssues, setDraftIssues] = useState<BuildEnrollmentIssue[]>([]);
  const [report, setReport] = useState<BuildPreflightReport | null>(null);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    buildApi.listProfiles()
      .then(setProfiles)
      .catch((err: unknown) => setLoadError(buildErrorMessage(err)))
      .finally(() => setLoaded(true));
  }, []);

  const profile = profiles.find((p) => p.id === profileId) ?? null;

  const roster = useMemo(() => parseRoster(rosterCsv), [rosterCsv]);
  const personas = useMemo(() => personasOf(roster.rows), [roster.rows]);
  const domains = useMemo(() => domainsOf(roster.rows), [roster.rows]);
  const overloaded = useMemo(() => overloadedDomains(roster.rows), [roster.rows]);

  /** The registrar this order will actually use, and whether it will ever be
   *  authenticated with. `cf_held` reaches no registrar at all, so an order on it
   *  borrows no registrar credential. A legacy `spaceship` profile is always
   *  Spaceship (the server ignores a pick there). */
  const registrarDns = profile ? registrarDnsOf(profile) : null;
  const registrar: BuildRegistrar = profile?.dns_mode === 'spaceship'
    ? 'spaceship' : registrarPick ?? profile?.registrar_default ?? 'none';
  const registrarName = REGISTRARS.find((r) => r.value === registrar)?.label ?? registrar;
  const registrarUsed = profile !== null && registrarIsUsed(profile.dns_mode, registrar);
  const registrarFields = REGISTRAR_CREDS_FIELDS[registrar] ?? [];

  const sequencerProvider = profile?.sequencer_provider ?? 'none';
  const miWorkspaceId = /^\d+$/.test(miWorkspaceText.trim())
    ? Number(miWorkspaceText.trim())
    : null;

  /**
   * MasterInbox is on without the two things it cannot be built without.
   *
   * This is reachable in exactly one way: the profile's default was OFF, so the
   * key and workspace fields were never rendered at step 2, and the operator
   * turned MasterInbox ON here. Drafting in that state is a guaranteed refusal,
   * and the missing workspace id is the worse half - an order that somehow got
   * past it would park EVERY mailbox permanently as `mi_workspace_undeclared`.
   * So it gates the Draft button, not just a notice.
   */
  const miIncomplete = miEnabled && (miKey.trim() === '' || miWorkspaceId === null);

  /** The map the snapshot carries, keyed by final address. THE R28 CONTRACT: one
   *  persona's face is written onto every address that persona owns, because the
   *  station matches nothing and expands nothing. */
  const images = useMemo((): Record<string, BuildPhotoEntry> => {
    const out: Record<string, BuildPhotoEntry> = {};
    for (const persona of personas) {
      const entry = photos[persona.key];
      if (!entry || entry.error || photoUrlProblem(entry)) continue;
      const value: BuildPhotoEntry | null = entry.b64
        ? { b64: entry.b64, ...(entry.mime ? { mime: entry.mime } : {}) }
        : entry.url && entry.url.trim() !== ''
          ? { url: entry.url.trim() }
          : null;
      if (value === null) continue;
      for (const email of persona.emails) out[email] = value;
    }
    return out;
  }, [personas, photos]);

  const photoedAddresses = Object.keys(images).length;

  /** Everything step 2 is missing. Blocking the Next button on these is not
   *  duplicated validation for its own sake: each one is a refusal the server
   *  would return AFTER the operator had left the field behind. */
  const step2Problems = useMemo((): string[] => {
    if (profile === null) return ['no profile is selected'];
    const problems: string[] = [];
    if (panelSplit.panel_bucket === 'mixed' && !validSerkenPercent(panelSplit.serken_percent ?? 50)) {
      problems.push('enter a whole Serken percentage from 0 to 100');
    }
    if (label.trim() === '') problems.push('the order needs a label');
    if (rosterCsv.trim() === '') {
      // An empty box has no lines to be wrong about; saying "1 line does not
      // parse" here would be nonsense, and the parser's own "the roster is
      // empty" issue is exactly that shape.
      problems.push('the roster is empty');
    } else {
      if (roster.rows.length === 0) problems.push('the roster has no usable rows');
      // A line that does not parse REFUSES THE WHOLE ENROLLMENT - the server
      // folds the parse issues straight into the refusal list and 400s if any
      // survive. It does not build the mailboxes it could read and skip the
      // rest, so this gate must not let an operator past a roster the order
      // cannot be made from.
      if (roster.issues.length > 0) {
        problems.push(
          `${roster.issues.length} roster line(s) do not parse; the whole enrollment is refused until every line reads`,
        );
      }
    }
    for (const { domain, count } of overloaded) {
      problems.push(
        `${domain} carries ${count} mailboxes; one domain lives on one panel and the ceiling is ${MAX_USERS_PER_DOMAIN}`,
      );
    }
    if (registrarUsed) {
      const missing = registrarFields.filter((f) => !(registrarCreds[f] ?? '').trim());
      if (missing.length > 0) problems.push(`the ${registrar} credential needs ${missing.join(', ')}`);
    }
    if (sequencerProvider !== 'none' && sequencerKey.trim() === '') {
      problems.push(`the sequencer provider is ${sequencerProvider}, so the order needs its api key`);
    }
    if (sequencerProvider === 'smartlead' && slUserId.trim() === '') {
      problems.push('Smartlead needs the workspace user id - press Probe with the key to read it');
    } else if (sequencerProvider === 'smartlead' && !SMARTLEAD_USER_ID.test(slUserId.trim())) {
      // Order 62 was enrolled with a UUID pasted from a failed probe's text; the
      // server refuses it now, and this says so before the field is left behind.
      problems.push(
        `"${slUserId.trim()}" is not a Smartlead user id - it is the workspace owner's number `
        + '(for example 659073), never a UUID or a campaign name; press Probe to read it',
      );
    }
    if (sequencerProvider === 'bison') {
      if (!/^https:\/\//i.test(bisonBaseUrl.trim())) {
        problems.push('Bison needs its https instance URL - the key only works against its own instance');
      }
      if (seqWorkspaceId.trim() === '') {
        problems.push('Bison needs the declared workspace - press Probe to list them');
      }
    }
    if (sequencerProvider === 'plusvibe') {
      if (seqWorkspaceId.trim() === '') {
        problems.push('PlusVibe needs the declared workspace - press Probe to list them');
      }
    }
    if (miEnabled && miKey.trim() === '') problems.push('MasterInbox is on, so the order needs its api key');
    if (miEnabled && miWorkspaceId === null) {
      problems.push('MasterInbox is on, so the order must declare the integer workspace id');
    }
    // Forwarding gates exactly as the server does. A profile with forwarding
    // disabled never shows the field, never demands a url and never submits one,
    // so it can never gate the step here (the earlier launch/draft refusal class).
    if (profile.forwarding.enabled) {
      if (profile.forwarding.require_target_url && forwardingUrl.trim() === '') {
        problems.push(`profile "${profile.name}" requires a forwarding target url`);
      }
      if (forwardingUrl.trim() !== '' && !/^https?:\/\//i.test(forwardingUrl.trim())) {
        problems.push('the forwarding url must be an http or https url');
      }
    }
    const badPhoto = personas.find((p) => photos[p.key]?.error);
    if (badPhoto) problems.push(`the photo for ${badPhoto.first_name} ${badPhoto.last_name} was rejected`);
    const badUrl = personas.find((p) => photoUrlProblem(photos[p.key]));
    if (badUrl) {
      problems.push(`the photo URL for ${badUrl.first_name} ${badUrl.last_name} must be https`);
    }
    return problems;
  }, [
    profile, label, rosterCsv, roster.rows.length, roster.issues.length, overloaded, registrarUsed,
    registrarFields, registrarCreds, registrar, sequencerProvider, sequencerKey, miEnabled, miKey,
    miWorkspaceId, forwardingUrl, personas, photos,
    slUserId, bisonBaseUrl, seqWorkspaceId, panelSplit,
  ]);

  /** Standard tags with `{DATE}` resolved exactly as the server will resolve it,
   *  plus whatever the operator picked. What the review step shows is what the
   *  station writes - minus Sub-Domain, which the server never writes run-wide. */
  const resolvedTags = useMemo(
    () => (profile === null ? [] : resolveTags(profile.tag_scheme.standard_tags, optionalTags)
      .filter((tag) => tag.toLowerCase() !== SUBDOMAIN_TAG.toLowerCase())),
    [profile, optionalTags],
  );

  function chooseProfile(picked: BuildProfile): void {
    setProfileId(picked.id);
    setPanelSplit({ panel_bucket: picked.panel_bucket, serken_percent: picked.serken_percent ?? 50 });
    // Every step-3 default comes from the profile at the moment it is picked, so
    // the review screen opens on the profile's own answer rather than a blank.
    setMiEnabled(picked.masterinbox.enabled_default);
    setPhotosEnabled(picked.photos.enabled_default);
    // The tracking-domain default is the PROFILE's (ctd_default), not a hardcoded
    // true. A profile with ctd_default off must round-trip to tracking_attach
    // false in the order; the server reads whatever the wizard sends verbatim, so
    // seeding true here would silently re-enable CTD on every order.
    setTrackingAttach(picked.ctd_default);
    setOptionalTags([]);
    setSettingsText('{}');
    setRegistrarPick(null);
    setRegistrarCreds({});
    setSlUserId('');
    setBisonBaseUrl('');
    setSeqWorkspaceId('');
    setSeqWorkspaces([]);
    setProbe(null);
    setProbeError(null);
  }

  function resetAll(): void {
    setStep(1);
    setProfileId(null);
    setLabel('');
    setRosterCsv('');
    setPanelSplit({ panel_bucket: 'serken', serken_percent: 50 });
    setRegistrarPick(null);
    setRegistrarCreds({});
    setSequencerKey('');
    setMiKey('');
    setMiWorkspaceText('');
    setMiWorkspaceName(null);
    setForwardingUrl('');
    setPhotos({});
    setSlUserId('');
    setBisonBaseUrl('');
    setSeqWorkspaceId('');
    setSeqWorkspaces([]);
    setProbe(null);
    setProbeError(null);
    setSettingsText('{}');
    setOptionalTags([]);
    setFreeTagDraft('');
    setDraft(null);
    setDraftError(null);
    setDraftIssues([]);
    setReport(null);
    setPreflightError(null);
  }

  async function runProbe(): Promise<void> {
    if (profile === null) return;
    setProbeBusy(true);
    setProbeError(null);
    try {
      const result = await buildApi.probeProfile(profile.id, {
        ...(sequencerKey.trim() ? { sequencer_api_key: sequencerKey.trim() } : {}),
        ...(sequencerProvider === 'smartlead' ? { resolve_smartlead_user_id: true } : {}),
        ...(miKey.trim() ? { masterinbox_api_key: miKey.trim() } : {}),
        ...(sequencerProvider === 'bison' && bisonBaseUrl.trim()
          ? { sequencer_base_url: bisonBaseUrl.trim() }
          : {}),
      });
      setProbe(result);
      // The whole point of probing from here: the MasterInbox workspace id the
      // snapshot has to declare is READ from the key, not remembered - and the
      // NAME rides along so the operator confirms a customer, not a number.
      if (result.masterinbox?.ok && result.masterinbox.workspace_id !== null) {
        setMiWorkspaceText(String(result.masterinbox.workspace_id));
        setMiWorkspaceName(result.masterinbox.workspace_name);
      }
      // Same pattern for the sequencer side: Smartlead's user id and the
      // plusvibe/bison workspace listings are resolved using the customer's key.
      if (result.sequencer?.ok) {
        // Only a Smartlead answer, and only a numeric one, may fill the id: the
        // server probes the PROFILE's provider, and an Instantly workspace id
        // (a UUID) must never land here if the two ever disagree.
        if (
          sequencerProvider === 'smartlead' && result.sequencer.provider === 'smartlead'
          && result.sequencer.workspace && SMARTLEAD_USER_ID.test(result.sequencer.workspace)
        ) {
          setSlUserId(result.sequencer.workspace);
        }
        const listed = result.sequencer.workspaces ?? [];
        if (listed.length > 0) {
          setSeqWorkspaces(listed);
          // One workspace = no decision to make; more = the picker opens blank.
          if (listed.length === 1) setSeqWorkspaceId(listed[0]?.id ?? '');
        }
      }
    } catch (err) {
      setProbeError(buildErrorMessage(err));
    } finally {
      setProbeBusy(false);
    }
  }

  async function createDraft(): Promise<void> {
    if (profile === null) return;
    setDraftError(null);
    setDraftIssues([]);

    let settings: Record<string, unknown>;
    try {
      const parsed: unknown = JSON.parse(settingsText.trim() === '' ? '{}' : settingsText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setDraftError('The sequencer settings must be a JSON object.');
        return;
      }
      settings = parsed as Record<string, unknown>;
    } catch (err) {
      setDraftError(`The sequencer settings are not valid JSON: ${buildErrorMessage(err)}`);
      return;
    }

    const payload: BuildOrderPayload = {
      profile_id: profile.id,
      label: label.trim(),
      roster_csv: rosterCsv,
      // Only a forwarding-enabled profile carries a target url. When forwarding is
      // off the station plans no redirect even if a url were present, so the order
      // must not carry one - it would describe a build this profile never does.
      ...(profile.forwarding.enabled && forwardingUrl.trim() !== ''
        ? { forwarding_url: forwardingUrl.trim() }
        : {}),
      overrides: {
        panel_bucket: panelSplit.panel_bucket,
        serken_percent: validSerkenPercent(panelSplit.serken_percent) ? panelSplit.serken_percent : 50,
        // Always explicit, even when it equals the profile default: the snapshot
        // is what the coordinator reads, and a later profile edit must not be
        // able to change which registrar this order delegates at.
        registrar,
        sequencer: {
          settings,
          optional_tags: optionalTags,
          tracking_attach: trackingAttach,
          ...(sequencerProvider === 'smartlead' && slUserId.trim()
            ? { sl_user_id: slUserId.trim() }
            : {}),
          ...(sequencerProvider === 'bison' && bisonBaseUrl.trim()
            ? { base_url: bisonBaseUrl.trim() }
            : {}),
          ...(sequencerProvider === 'bison' && seqWorkspaceId.trim()
            ? { workspace_id: seqWorkspaceId.trim() }
            : {}),
          ...(sequencerProvider === 'plusvibe' && seqWorkspaceId.trim()
            ? { pv_workspace_id: seqWorkspaceId.trim() }
            : {}),
        },
        masterinbox: {
          enabled: miEnabled,
          app_id: MI_ALLOWED_APP_ID,
          ...(miEnabled && miWorkspaceId !== null ? { workspace_id: miWorkspaceId } : {}),
        },
        photos: {
          enabled: photosEnabled,
          // Photos off means the map is not worth sending; the station reads the
          // block as off and stamps nothing.
          ...(photosEnabled ? { images } : {}),
        },
      },
      secrets: {
        ...(registrarUsed ? { registrar_creds: registrarCreds } : {}),
        ...(sequencerProvider !== 'none' && sequencerKey.trim()
          ? { sequencer_api_key: sequencerKey.trim() }
          : {}),
        ...(miEnabled && miKey.trim() ? { masterinbox_api_key: miKey.trim() } : {}),
      },
    };

    setDrafting(true);
    try {
      const result = await buildApi.createOrder(payload);
      setDraft(result);
      setStep(4);
      showToast(
        'success',
        `Order ${result.order.id} drafted: ${result.roster.mailboxes} mailbox(es) on ${result.roster.domains} domain(s).`,
      );
      for (const note of result.notes ?? []) showToast('info', note);
    } catch (err) {
      setDraftIssues(buildIssues(err));
      setDraftError(buildErrorMessage(err));
      showToast('error', buildErrorMessage(err));
    } finally {
      setDrafting(false);
    }
  }

  const runPreflight = useCallback(async (orderId: number): Promise<void> => {
    setPreflightBusy(true);
    setPreflightError(null);
    setReport(null);
    try {
      const result = await buildApi.preflight(orderId);
      setReport(result);
      showToast(
        result.pass ? 'success' : 'warn',
        result.pass ? 'Preflight passed. The order is cleared to launch.' : 'Preflight failed - read the gates below.',
      );
    } catch (err) {
      setPreflightError(buildErrorMessage(err));
      showToast('error', buildErrorMessage(err));
    } finally {
      setPreflightBusy(false);
    }
  }, [showToast]);

  /**
   * Preflight is not optional - the launch gate refuses an order without a
   * passing report - so entering step 4 starts it rather than waiting for a
   * second click. The ref is what keeps it to ONE run per draft: this fires
   * again on every re-render caused by the run itself, and in development
   * StrictMode mounts the effect twice.
   */
  const autoFlown = useRef<number | null>(null);
  useEffect(() => {
    const orderId = draft?.order.id;
    if (step !== 4 || orderId === undefined || autoFlown.current === orderId) return;
    autoFlown.current = orderId;
    void runPreflight(orderId);
  }, [step, draft, runPreflight]);

  async function launch(): Promise<void> {
    if (draft === null) return;
    setLaunching(true);
    try {
      const result = await buildApi.launch(draft.order.id);
      const placed = result.allocated_panels.length;
      showToast(
        result.shortfall.length > 0 ? 'warn' : 'success',
        result.shortfall.length > 0
          ? `Order ${draft.order.id} launched on ${placed} panel(s), ${result.shortfall.length} domain(s) short of capacity - the coordinator re-plans them every 10 minutes.`
          : `Order ${draft.order.id} launched on ${placed} panel(s). The coordinator owns it from here.`,
      );
      if (result.allocation_error) {
        showToast('warn', `Allocation threw at launch and will be retried on the next tick: ${result.allocation_error}`);
      }
      const orderId = draft.order.id;
      resetAll();
      onLaunched(orderId);
    } catch (err) {
      showToast('error', buildErrorMessage(err));
    } finally {
      setLaunching(false);
    }
  }

  // Once a draft exists the roster and the credentials are encrypted onto a row;
  // editing the form behind it would describe an order that does not exist.
  const locked = draft !== null;

  return (
    <>
      {loadError && <div className="bl-toast bl-toast--error">Could not load profiles: {loadError}</div>}

      <nav className="bl-steps">
        {STEPS.map((s) => (
          <button
            key={s.n}
            type="button"
            className={`bl-step ${step === s.n ? 'active' : ''} ${step > s.n ? 'done' : ''}`}
            disabled={s.n > step || (locked && s.n < 4)}
            onClick={() => setStep(s.n)}
          >
            <span className="bl-step-n">{s.n}</span>
            {s.label}
          </button>
        ))}
      </nav>

      {step === 1 && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">1. Pick a profile</h2>
          </div>
          <div className="bl-card-body">
            <span className="bl-hint">
              The profile decides where the zones live, which registrar delegates them, which panel
              bucket the seats come from and what the sequencer is configured to. Enrollment freezes
              it into this order&apos;s snapshot, so a later edit to the profile cannot change what
              this build does.
            </span>
            {profiles.length === 0 ? (
              <div className="bl-empty">
                {!loaded
                  ? 'Loading profiles...'
                  : loadError !== null
                    ? 'The profile list could not be loaded - this is not an empty list.'
                    : 'No profiles yet. Create one on the Profiles view first.'}
              </div>
            ) : (
              <fieldset className="bl-profile-options">
                <legend className="bl-profile-sr-only">Available profiles</legend>
                {profiles.map((p) => (
                  <label key={p.id} className={`bl-profile-option ${profileId === p.id ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="build-enroll-profile"
                      value={p.id}
                      checked={profileId === p.id}
                      onChange={() => chooseProfile(p)}
                      aria-labelledby={`bl-profile-name-${p.id} bl-profile-provider-${p.id}`}
                      aria-describedby={`bl-profile-details-${p.id}`}
                    />
                    <span className="bl-profile-option-content">
                      <span className="bl-profile-option-heading">
                        <span className="bl-profile-option-name" id={`bl-profile-name-${p.id}`}>{p.name}</span>
                        <span className="bl-profile-option-customer">{p.customer}</span>
                      </span>
                      <span className="bl-profile-option-provider" id={`bl-profile-provider-${p.id}`}>
                        {{ instantly: 'Instantly', plusvibe: 'PlusVibe', smartlead: 'Smartlead', bison: 'Bison', none: 'No sequencer' }[p.sequencer_provider]}
                      </span>
                      <span className="bl-profile-option-details" id={`bl-profile-details-${p.id}`}>
                        <span>
                          <span className="bl-profile-detail-label">DNS hosting</span>
                          <span>{hostsAtRegistrar(p.dns_mode)
                            ? `Use ${REGISTRARS.find((r) => r.value === registrarDnsOf(p))?.label ?? 'registrar'} DNS`
                            : { cf_held: 'Use existing zones', our_pool: 'Create in our account pool', customer_cf: 'Use customer account' }[p.dns_mode as 'cf_held' | 'our_pool' | 'customer_cf']}</span>
                        </span>
                        <span>
                          <span className="bl-profile-detail-label">Mailbox panels</span>
                          <span>{panelSplitLabel(p)}</span>
                          <span className="bl-profile-detail-secondary">{p.allocation_policy === 'exact_fill' ? 'Exact fill' : 'Leave 0 or 2 spare seats'}</span>
                        </span>
                        <span>
                          <span className="bl-profile-detail-label">DNS</span>
                          <span>{hostsAtRegistrar(p.dns_mode) ? 'At the registrar, no Cloudflare account'
                            : p.dns_mode === 'our_pool' ? 'Our Cloudflare pool' : `Cloudflare account ${p.cf_account_id ?? 'not selected'}`}</span>
                          <span className="bl-profile-detail-secondary">MX {p.records.write_mx ? 'enabled' : 'disabled'}</span>
                        </span>
                        <span>
                          <span className="bl-profile-detail-label">MasterInbox</span>
                          <span>{p.masterinbox.enabled_default ? 'Enabled' : 'Off'}</span>
                        </span>
                        <span>
                          <span className="bl-profile-detail-label">Photos</span>
                          <span>{p.photos.enabled_default ? 'Enabled' : 'Off'}</span>
                        </span>
                        <span>
                          <span className="bl-profile-detail-label">Forwarding</span>
                          <span>{p.forwarding.enabled ? (p.forwarding.require_target_url ? 'URL required' : 'URL optional') : 'Off'}</span>
                        </span>
                      </span>
                      {p.notes.trim() !== '' && <span className="bl-profile-option-notes">{p.notes}</span>}
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
            <div className="bl-actions">
              <button
                type="button"
                className="bl-btn bl-btn--primary"
                disabled={profile === null}
                onClick={() => setStep(2)}
              >
                Next: roster and credentials
              </button>
            </div>
          </div>
        </section>
      )}

      {step === 2 && profile !== null && (
        <>
          <section className="bl-card">
            <div className="bl-card-header">
              <h2 className="bl-card-title">2. Roster</h2>
              <span className="bl-cell-faint">profile {profile.name}</span>
            </div>
            <div className="bl-card-body">
              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-en-label">Order label</label>
                <input
                  id="bl-en-label"
                  className="bl-input"
                  value={label}
                  maxLength={200}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="cold-labs 300 - 2026-08-30"
                />
                <span className="bl-hint">How this order is named everywhere an operator sees it.</span>
              </div>

              <div className="bl-field">
                <label className="bl-field-label" htmlFor="bl-en-roster">Roster CSV</label>
                <textarea
                  id="bl-en-roster"
                  className="bl-textarea bl-textarea--tall"
                  value={rosterCsv}
                  onChange={(e) => setRosterCsv(e.target.value)}
                  spellCheck={false}
                  placeholder={'email,first,last,password,group\ndrew@askalpha.com,Drew,Hart,Str0ng-Password!,Active'}
                />
                <span className="bl-hint">
                  Both shapes are accepted: Google&apos;s bulk-upload export (
                  <code>First Name [Required]</code>, <code>Email Address [Required]</code>, ...) and
                  the plain <code>email,first,last,password[,group]</code> an operator types. A header
                  row is detected; without one the plain positional order is assumed. Quoted fields
                  are honoured, because a password legitimately contains commas.
                </span>
              </div>

              {rosterCsv.trim() !== '' && (
                <div className="bl-statrow">
                  <div className="bl-stat">
                    <span className="bl-stat-n">{roster.rows.length}</span>
                    <span className="bl-stat-label">mailboxes</span>
                  </div>
                  <div className="bl-stat">
                    <span className="bl-stat-n">{domains.length}</span>
                    <span className="bl-stat-label">domains</span>
                  </div>
                  <div className="bl-stat">
                    <span className="bl-stat-n">{personas.length}</span>
                    <span className="bl-stat-label">personas</span>
                  </div>
                  <div className="bl-stat">
                    <span className="bl-stat-n">{roster.issues.length}</span>
                    <span className="bl-stat-label">bad lines</span>
                  </div>
                </div>
              )}

              <div className="bl-section-title">Panel split for this order</div>
              <PanelSplitFields id="bl-en" value={panelSplit}
                onChange={(change) => setPanelSplit((current) => ({ ...current, ...change }))} />
              <span className="bl-hint">Starts from the profile default. Changes here apply only to this order.</span>
              <PanelSplitSummary rows={roster.rows} split={panelSplit} />

              {rosterCsv.trim() !== '' && roster.issues.length > 0 && (
                <div className="bl-notice bl-notice--err">
                  <strong>
                    These lines do not parse, so the enrollment is refused until they are fixed.
                  </strong>{' '}
                  They are not skipped and the readable lines are not built without them: a roster
                  that quietly shrinks by two lines is an order that ships two mailboxes short, so
                  the whole order is held back instead. Correct them in the box above or delete them.
                  {roster.issues.map((issue, i) => (
                    <div key={`${issue.path}-${i}`}>
                      <code>{issue.path}</code> - {issue.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="bl-card">
            <div className="bl-card-header">
              <h2 className="bl-card-title">Credentials this order borrows</h2>
              <div className="bl-actions">
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  disabled={probeBusy}
                  onClick={() => void runProbe()}
                >
                  {probeBusy ? 'Probing...' : 'Probe these keys'}
                </button>
              </div>
            </div>
            <div className="bl-card-body">
              <span className="bl-hint">
                Credentials are encrypted onto the order and purged when it finishes.
                Probing does not save the keys and fills in the account details below.
                {sequencerProvider === 'smartlead'
                  ? ' If Smartlead has no user ID to read, probing creates a temporary empty draft campaign and removes it after finding the ID.'
                  : ' Probing is read-only.'}
              </span>

              {profile.dns_mode !== 'cf_held' && profile.dns_mode !== 'spaceship' && (
                <div className="bl-formgrid">
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-en-registrar">
                      Registrar for this order
                    </label>
                    <select
                      id="bl-en-registrar"
                      className="bl-input"
                      value={registrar}
                      onChange={(e) => {
                        setRegistrarPick(e.target.value as BuildRegistrar);
                        // A key typed for the other registrar has the wrong shape
                        // for this one, and must never ride along.
                        setRegistrarCreds({});
                      }}
                    >
                      {REGISTRARS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      {/* Registrar DNS writes AT the registrar, so it always needs one. */}
                      {registrarDns === null && <option value="none">none - domains already routed</option>}
                    </select>
                    <span className="bl-hint">
                      {registrarDns !== null ? <>
                        Adds mail DNS at {registrarName} with the credential below. Each parent domain must
                        already use {registrarName}&apos;s own nameservers; nameservers and website records
                        are left unchanged. Use one registrar account per order.
                      </> : registrar === 'none' ? 'Parent domains must already point to the selected Cloudflare account.' : <>
                        Creates or reuses each parent domain&apos;s zone in the selected Cloudflare account,
                        then updates nameservers at {registrarName} only where needed. Mixed root and subdomain
                        email addresses are supported; shared parents are routed once, even without a parent-domain inbox.
                        Use one registrar account per order.
                      </>}
                      {' '}{REGISTRARS.find((r) => r.value === registrar)?.hint}
                    </span>
                  </div>
                </div>
              )}

              {registrarUsed ? (
                <div className="bl-formgrid">
                  {registrarFields.map((field) => (
                    <div className="bl-field" key={field}>
                      <label className="bl-field-label" htmlFor={`bl-en-reg-${field}`}>
                        {registrarDns !== null ? `${registrarName} DNS API` : registrarName} {field}
                      </label>
                      <input
                        id={`bl-en-reg-${field}`}
                        className="bl-input bl-mono"
                        type="password"
                        autoComplete="off"
                        value={registrarCreds[field] ?? ''}
                        onChange={(e) =>
                          setRegistrarCreds((current) => ({ ...current, [field]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bl-notice">
                  No registrar credential. <code>{profile.dns_mode}</code> with registrar{' '}
                  <code>{registrar}</code> never authenticates at one, and a customer key nothing
                  will use is one we should not be holding.
                </div>
              )}

              {sequencerProvider !== 'none' && (
                <div className="bl-formgrid">
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-en-seq">
                      {sequencerProvider} api key
                    </label>
                    <input
                      id="bl-en-seq"
                      className="bl-input bl-mono"
                      type="password"
                      autoComplete="off"
                      value={sequencerKey}
                      disabled={probeBusy}
                      onChange={(e) => {
                        setSequencerKey(e.target.value);
                        setSlUserId('');
                        setSeqWorkspaceId('');
                        setSeqWorkspaces([]);
                        setProbe((current) => current ? { ...current, sequencer: null } : null);
                      }}
                    />
                    <span className="bl-hint">One key is one workspace. Probe it to see which.</span>
                  </div>
                  <div className="bl-field">
                    {/* Smartlead's probe reads the workspace USER id (the consent
                        URL's raw material), not a workspace - label it honestly. */}
                    <span className="bl-field-label">
                      {sequencerProvider === 'smartlead' ? 'User ID' : 'Workspace'}
                    </span>
                    <span className={`bl-readout ${probe?.sequencer && !probe.sequencer.ok ? 'bl-readout--bad' : ''}`}>
                      {probe?.sequencer
                        ? probe.sequencer.ok
                          ? probe.sequencer.workspace
                            ?? (seqWorkspaces.length > 0 ? `${seqWorkspaces.length} listed below` : 'unnamed')
                          : probe.sequencer.error ?? 'the key was refused'
                        : 'not probed'}
                    </span>
                  </div>

                  {sequencerProvider === 'smartlead' && (
                    <div className="bl-field">
                      <label className="bl-field-label" htmlFor="bl-en-sluid">Smartlead user id</label>
                      <input
                        id="bl-en-sluid"
                        className="bl-input bl-mono"
                        inputMode="numeric"
                        value={slUserId}
                        onChange={(e) => setSlUserId(e.target.value)}
                        placeholder="read by Probe"
                        aria-invalid={slUserId.trim() !== '' && !SMARTLEAD_USER_ID.test(slUserId.trim())}
                      />
                      <span className="bl-hint">
                        Used to connect inboxes to the correct Smartlead account. Digits only: the
                        workspace owner&apos;s number, never a UUID or a campaign name. Probe these
                        keys to fill it in, including for an empty account.
                      </span>
                    </div>
                  )}

                  {sequencerProvider === 'bison' && (
                    <>
                      <div className="bl-field">
                        <label className="bl-field-label" htmlFor="bl-en-bison-url">Bison instance URL</label>
                        <input
                          id="bl-en-bison-url"
                          className="bl-input bl-mono"
                          value={bisonBaseUrl}
                          onChange={(e) => {
                            setBisonBaseUrl(e.target.value);
                            // A listing read against one instance must not vouch
                            // for a different one.
                            setSeqWorkspaces([]);
                            setSeqWorkspaceId('');
                          }}
                          placeholder="https://bison.customer.example"
                        />
                        <span className="bl-hint">
                          White-label: the key only works against its own instance. Probe checks
                          the PAIR and lists the workspaces.
                        </span>
                      </div>
                      <div className="bl-field">
                        <label className="bl-field-label" htmlFor="bl-en-seq-ws">Workspace</label>
                        <select
                          id="bl-en-seq-ws"
                          className="bl-input"
                          value={seqWorkspaceId}
                          onChange={(e) => setSeqWorkspaceId(e.target.value)}
                        >
                          <option value="">pick after Probe</option>
                          {seqWorkspaces.map((w) => (
                            <option key={w.id} value={w.id}>{w.name} ({w.id})</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {sequencerProvider === 'plusvibe' && (
                    <>
                      <div className="bl-field">
                        <label className="bl-field-label" htmlFor="bl-en-seq-ws">Workspace</label>
                        <select
                          id="bl-en-seq-ws"
                          className="bl-input"
                          value={seqWorkspaceId}
                          onChange={(e) => setSeqWorkspaceId(e.target.value)}
                        >
                          <option value="">pick after Probe</option>
                          {seqWorkspaces.map((w) => (
                            <option key={w.id} value={w.id}>{w.name} ({w.id})</option>
                          ))}
                        </select>
                        <span className="bl-hint">
                          Read off the key by Probe - the station refuses a workspace the key
                          cannot list.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {miEnabled && (
                <div className="bl-formgrid">
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-en-mi">MasterInbox api key</label>
                    <input
                      id="bl-en-mi"
                      className="bl-input bl-mono"
                      type="password"
                      autoComplete="off"
                      value={miKey}
                      onChange={(e) => {
                        setMiKey(e.target.value);
                        // A resolved name must never vouch for a DIFFERENT key.
                        setMiWorkspaceName(null);
                      }}
                    />
                    <span className="bl-hint">
                      Connected through <code>{MI_ALLOWED_APP_ID}</code>, which is pinned.
                    </span>
                  </div>
                  <div className="bl-field">
                    <label className="bl-field-label" htmlFor="bl-en-miws">MasterInbox workspace id</label>
                    <input
                      id="bl-en-miws"
                      className="bl-input bl-mono"
                      inputMode="numeric"
                      value={miWorkspaceText}
                      onChange={(e) => setMiWorkspaceText(e.target.value)}
                      placeholder="2464"
                    />
                    {miWorkspaceName !== null && (
                      <span className="bl-readout">
                        Key points at workspace {miWorkspaceText || '?'}
                        {' - '}
                        {miWorkspaceName || 'unnamed'}
                      </span>
                    )}
                    <span className="bl-hint">
                      Required while MasterInbox is on - without it every mailbox on this order
                      parks PERMANENTLY as <code>mi_workspace_undeclared</code>. Press Probe with
                      the key above: the id AND the workspace name are read straight off the key,
                      so you confirm a customer, never a bare number.
                    </span>
                  </div>
                </div>
              )}

              {profile.forwarding.enabled ? (
                <div className="bl-field">
                  <label className="bl-field-label" htmlFor="bl-en-fwd">
                    Forwarding target URL{profile.forwarding.require_target_url ? ' (required)' : ' (optional)'}
                  </label>
                  <input
                    id="bl-en-fwd"
                    className="bl-input bl-mono"
                    value={forwardingUrl}
                    onChange={(e) => setForwardingUrl(e.target.value)}
                    placeholder="https://www.example.com/"
                  />
                  <span className="bl-hint">
                    Turned into a Cloudflare redirect rule per domain. http or https only.
                  </span>
                </div>
              ) : (
                <div className="bl-notice">
                  No forwarding. This profile stands up no redirect, so no target URL is
                  borrowed and nothing on this order forwards.
                </div>
              )}

              {probeError !== null && (
                <div className="bl-notice bl-notice--err">
                  <strong>The probe could not run:</strong> {probeError}
                </div>
              )}
              {probe !== null && (
                <div className="bl-probe">
                  {probe.cloudflare ? <div className={`bl-probe-row ${probe.cloudflare.ok ? 'bl-probe-row--ok' : 'bl-probe-row--bad'}`}>
                    <span className="bl-probe-mark">{probe.cloudflare.ok ? 'PASS' : 'FAIL'}</span>
                    <span className="bl-probe-label">Cloudflare</span>
                    <span className="bl-probe-detail">
                      {probe.cloudflare.ok
                        ? `account ${probe.cloudflare.account_id ?? 'unknown'} - ${probe.cloudflare.zones ?? 0} zone(s) visible`
                        : probe.cloudflare.error ?? 'failed'}
                    </span>
                  </div>
                  : <div className="bl-notice">Spaceship DNS access is checked for every roster domain during the dry run.</div>}
                  {probe.masterinbox !== null && (
                    <div className={`bl-probe-row ${probe.masterinbox.ok ? 'bl-probe-row--ok' : 'bl-probe-row--bad'}`}>
                      <span className="bl-probe-mark">{probe.masterinbox.ok ? 'PASS' : 'FAIL'}</span>
                      <span className="bl-probe-label">MasterInbox ({probe.masterinbox.app_id})</span>
                      <span className="bl-probe-detail">
                        {probe.masterinbox.ok
                          ? `workspace ${probe.masterinbox.workspace_id ?? 'unknown'}`
                            + (probe.masterinbox.workspace_name ? ` - ${probe.masterinbox.workspace_name}` : '')
                          : probe.masterinbox.error ?? 'failed'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          <section className="bl-card">
            <div className="bl-card-header">
              <h2 className="bl-card-title">Photos, one per persona</h2>
              <span className="bl-cell-faint">
                {photoedAddresses} of {roster.rows.length} mailboxes covered
              </span>
            </div>
            <div className="bl-card-body">
              <span className="bl-hint">
                A persona is one identity - the same first and last name - and it usually sends from
                several domains. Supply one face per persona and it is written onto EVERY address
                that persona owns before the order is submitted, because the station reads an
                address-keyed map and matches nothing itself. An https URL or an inline file, up to{' '}
                {Math.round(MAX_PHOTO_BYTES / 1024 / 1024)} MB each.
              </span>

              {personas.length === 0 ? (
                <div className="bl-empty">Paste a roster above and the personas appear here.</div>
              ) : (
                <div className="bl-table-wrap">
                  <table className="bl-table">
                    <thead>
                      <tr>
                        <th>Persona</th>
                        <th>Sends as</th>
                        <th className="bl-col-center">Addresses</th>
                        <th>Photo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personas.map((persona) => (
                        <PersonaRow
                          key={persona.key}
                          persona={persona}
                          photo={photos[persona.key]}
                          onChange={(next) =>
                            setPhotos((current) => ({ ...current, [persona.key]: next }))}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>

          <section className="bl-card">
            <div className="bl-card-body">
              {step2Problems.length > 0 && (
                <div className="bl-notice bl-notice--warn">
                  <strong>Still needed:</strong>
                  {step2Problems.map((problem, i) => <div key={i}>{problem}</div>)}
                </div>
              )}
              <div className="bl-actions">
                <button type="button" className="bl-btn bl-btn--ghost" onClick={() => setStep(1)}>
                  Back
                </button>
                <button
                  type="button"
                  className="bl-btn bl-btn--primary"
                  disabled={step2Problems.length > 0}
                  onClick={() => setStep(3)}
                >
                  Next: review the snapshot
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {step === 3 && profile !== null && (
        <section className="bl-card">
          <div className="bl-card-header">
            <h2 className="bl-card-title">3. The snapshot</h2>
            <span className="bl-cell-faint">
              {roster.rows.length} mailboxes - {domains.length} domains
            </span>
          </div>
          <div className="bl-card-body">
            <span className="bl-hint">
              This is the only thing the coordinator reads for the life of the build. Editing the
              profile afterwards cannot change it, and nothing downstream falls back to the profile
              for anything decided here.
            </span>

            {draftError !== null && (
              <div className="bl-notice bl-notice--err">
                <strong>Refused:</strong> {draftError}
              </div>
            )}
            {draftIssues.length > 0 && (
              <div className="bl-notice bl-notice--err">
                {draftIssues.map((issue, i) => (
                  <div key={`${issue.path}-${i}`}>
                    <code>{issue.path}</code> - {issue.message}
                  </div>
                ))}
              </div>
            )}

            <PanelSplitSummary rows={roster.rows} split={panelSplit} />

            <div className="bl-section-title">Sequencer</div>
            <div className="bl-field">
              <span className="bl-field-label">Provider</span>
              <span className="bl-readout">{sequencerProvider}</span>
            </div>
            <div className="bl-field">
              <span className="bl-field-label">Profile baseline (read-only)</span>
              <pre className="bl-pre">{JSON.stringify(profile.sequencer_settings, null, 2)}</pre>
            </div>
            <div className="bl-field">
              <label className="bl-field-label" htmlFor="bl-en-settings">Settings override (JSON)</label>
              <textarea
                id="bl-en-settings"
                className="bl-textarea bl-textarea--code"
                value={settingsText}
                onChange={(e) => setSettingsText(e.target.value)}
                spellCheck={false}
              />
              <span className="bl-hint">
                A PATCH on the baseline above, never a replacement: only the keys you write here are
                changed, at every level. Leave it <code>{'{}'}</code> to build on the profile exactly
                as it stands.
              </span>
            </div>
            {sequencerProvider === 'instantly' && (
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-en-tracking">
                  <input
                    id="bl-en-tracking"
                    type="checkbox"
                    checked={trackingAttach}
                    onChange={(e) => setTrackingAttach(e.target.checked)}
                  />
                  Attach the tracking domain to each account
                </label>
                <span className="bl-hint">
                  Attaches <code>{profile.records.tracking_prefix || 'inst'}.&lt;domain&gt;</code> to each account.
                  Enable the matching tracking CNAME in the profile’s DNS records.
                </span>
              </div>
            )}

            <div className="bl-section-title">Tags</div>
            <div className="bl-field">
              <span className="bl-field-label">Standard (locked)</span>
              <div className="bl-chips">
                {profile.tag_scheme.standard_tags.length === 0 && <span className="bl-hint">none</span>}
                {profile.tag_scheme.standard_tags.map((tag) => (
                  <span key={tag} className="bl-chip bl-chip--locked">{tag}</span>
                ))}
              </div>
              <span className="bl-hint">
                Applied to every order built on this profile. Any <code>{'{DATE}'}</code> resolves at
                submit; the resolved list is shown below.
              </span>
            </div>
            {profile.tag_scheme.optional_tags.some((tag) => tag.toLowerCase() !== SUBDOMAIN_TAG.toLowerCase()) && (
              <div className="bl-field">
                <span className="bl-field-label">Optional</span>
                <div className="bl-checkrow">
                  {profile.tag_scheme.optional_tags.filter((tag) => tag.toLowerCase() !== SUBDOMAIN_TAG.toLowerCase()).map((tag) => (
                    <label className="bl-check" key={tag}>
                      <input
                        type="checkbox"
                        checked={optionalTags.includes(tag)}
                        onChange={(e) =>
                          setOptionalTags((current) =>
                            e.target.checked
                              ? [...current, tag]
                              : current.filter((t) => t !== tag))}
                      />
                      {tag}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="bl-field">
              <span className="bl-field-label">Add a tag</span>
              <div className="bl-inline-add">
                <input
                  className="bl-input"
                  value={freeTagDraft}
                  onChange={(e) => setFreeTagDraft(e.target.value)}
                  placeholder="anything this batch needs"
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    const tag = freeTagDraft.trim();
                    if (tag !== '' && !optionalTags.includes(tag)) setOptionalTags((c) => [...c, tag]);
                    setFreeTagDraft('');
                  }}
                />
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  onClick={() => {
                    const tag = freeTagDraft.trim();
                    if (tag !== '' && !optionalTags.includes(tag)) setOptionalTags((c) => [...c, tag]);
                    setFreeTagDraft('');
                  }}
                >
                  Add
                </button>
              </div>
              <div className="bl-chips">
                {optionalTags.map((tag) => (
                  <span key={tag} className="bl-chip">
                    {tag}
                    <button
                      type="button"
                      className="bl-chip-x"
                      onClick={() => setOptionalTags((c) => c.filter((t) => t !== tag))}
                      aria-label={`remove ${tag}`}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="bl-field">
              <span className="bl-field-label">What the station will write</span>
              <div className="bl-chips">
                {resolvedTags.length === 0 && <span className="bl-hint">no tags</span>}
                {resolvedTags.map((tag) => <span key={tag} className="bl-chip bl-chip--resolved">{tag}</span>)}
              </div>
              <span className="bl-hint">Sub-Domain is applied automatically to subdomain mailboxes.</span>
              {profile.tag_scheme.group_tags_enabled && (
                <span className="bl-hint">
                  Group tags are on for this profile: each mailbox also gets its roster group as one
                  extra tag.
                </span>
              )}
            </div>

            <div className="bl-section-title">MasterInbox and photos</div>
            <div className="bl-formgrid">
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-en-mion">
                  <input
                    id="bl-en-mion"
                    type="checkbox"
                    checked={miEnabled}
                    onChange={(e) => setMiEnabled(e.target.checked)}
                  />
                  Connect to MasterInbox
                </label>
                <span className="bl-hint">
                  {miEnabled
                    ? `workspace ${miWorkspaceId ?? 'NOT DECLARED'}, app ${MI_ALLOWED_APP_ID}`
                    : 'off, so no MasterInbox key is borrowed and the station stamps nothing'}
                </span>
              </div>
              <div className="bl-field">
                <label className="bl-check" htmlFor="bl-en-photoson">
                  <input
                    id="bl-en-photoson"
                    type="checkbox"
                    checked={photosEnabled}
                    onChange={(e) => setPhotosEnabled(e.target.checked)}
                  />
                  Set profile photos
                </label>
                <span className="bl-hint">
                  {photosEnabled
                    ? `${photoedAddresses} of ${roster.rows.length} mailboxes carry an image`
                    : 'off, so every mailbox ships photoless'}
                </span>
              </div>
            </div>
            {miIncomplete && (
              <div className="bl-notice bl-notice--err">
                <strong>
                  MasterInbox is on but {miKey.trim() === '' ? 'no api key' : 'no integer workspace id'}{' '}
                  was supplied.
                </strong>{' '}
                Its fields were not shown at step 2 because this profile has MasterInbox off by
                default. Go back and fill them in - drafting is blocked until you do, because the
                order would be refused outright, and a missing workspace id is the one mistake that
                would park every mailbox permanently.
              </div>
            )}
            {photosEnabled && photoedAddresses === 0 && (
              <div className="bl-notice bl-notice--warn">
                Photos are on and nothing was uploaded. That is allowed - every mailbox on this
                order will simply be stamped photoless - but if you meant to supply faces, go back to
                step 2 now.
              </div>
            )}
            {photosEnabled && photoedAddresses > 0 && photoedAddresses < roster.rows.length && (
              <div className="bl-notice bl-notice--warn">
                {roster.rows.length - photoedAddresses} mailbox(es) have no image and will ship
                photoless.
              </div>
            )}

            <div className="bl-actions">
              <button
                type="button"
                className="bl-btn bl-btn--ghost"
                disabled={drafting}
                onClick={() => setStep(2)}
              >
                Back
              </button>
              <button
                type="button"
                className="bl-btn bl-btn--primary"
                disabled={drafting || miIncomplete}
                onClick={() => void createDraft()}
              >
                {drafting ? 'Drafting...' : 'Draft the order and preflight it'}
              </button>
            </div>
          </div>
        </section>
      )}

      {step === 4 && draft !== null && (
        <>
          <section className="bl-card">
            <div className="bl-card-header">
              <h2 className="bl-card-title">4. Preflight</h2>
              <div className="bl-actions">
                <button
                  type="button"
                  className="bl-btn bl-btn--secondary bl-btn--small"
                  disabled={preflightBusy}
                  onClick={() => void runPreflight(draft.order.id)}
                >
                  {preflightBusy ? 'Running...' : 'Run preflight again'}
                </button>
              </div>
            </div>
            <div className="bl-card-body">
              <div className="bl-notice bl-notice--info">
                <strong>Order {draft.order.id} is drafted:</strong> {draft.roster.mailboxes}{' '}
                mailbox(es) on {draft.roster.domains} domain(s), and its borrowed credentials are
                encrypted onto the row. Nothing has been bought, claimed or written.
              </div>

              {preflightBusy && (
                <div className="bl-notice bl-notice--info">
                  Refreshing panel inventory and checking DNS, Google, the sequencer and MasterInbox.
                  The allocation preview waits for the inventory pulls to finish. This can take
                  one to three minutes; no panels or licenses are reserved. Leave the tab open.
                </div>
              )}
              {preflightError !== null && (
                <div className="bl-notice bl-notice--err">
                  <strong>The preflight could not run:</strong> {preflightError}
                </div>
              )}

              {report !== null && (
                <>
                  <div className={`bl-verdict ${report.pass ? 'bl-verdict--ok' : 'bl-verdict--bad'}`}>
                    {report.pass ? 'PASS' : 'FAIL'}
                    <span className="bl-verdict-sub">
                      checked at {new Date(report.checked_at).toLocaleString()}
                    </span>
                  </div>

                  <Gate name="Input" result={report.input} />
                  <Gate
                    name={`DNS coverage (${report.dns.mode})`}
                    result={report.dns}
                    detail={`${report.dns.checked} root(s) checked`}
                  />
                  <Gate
                    name="Google ownership"
                    result={report.google}
                    detail={[
                      report.google.conflicts.length > 0
                        ? `${report.google.conflicts.length} conflict(s)`
                        : '',
                      report.google.unverified.length > 0
                        ? `${report.google.unverified.length} unverified`
                        : '',
                    ].filter(Boolean).join(', ')}
                  />
                  <Gate
                    name="Keys"
                    result={report.keys}
                    detail={[
                      report.keys.registrar
                        ? `${report.keys.registrar.registrar}: ${report.keys.registrar.checked} domain(s)`
                        : '',
                      report.keys.sequencer
                        ? `${report.keys.sequencer.provider}: workspace ${report.keys.sequencer.workspace ?? 'unnamed'}`
                        : '',
                      report.keys.masterinbox
                        ? `MasterInbox: key workspace ${report.keys.masterinbox.workspace_id}`
                          + (report.keys.masterinbox.workspace_name ? ` (${report.keys.masterinbox.workspace_name})` : '')
                          + `, declared ${report.keys.masterinbox.declared ?? 'none'}`
                        : '',
                    ].filter(Boolean).join(' - ')}
                  />
                  <Gate
                    name="Panel capacity (reported, not gated)"
                    result={report.panels}
                    soft
                    detail={`${report.panels.pool_panels} panel(s), ${report.panels.free_seats} free seat(s)`
                      + `${report.panels.pulled_at ? `, pulled ${new Date(report.panels.pulled_at).toLocaleString()}` : ''}`}
                  />
                </>
              )}
            </div>
          </section>

          {report !== null && (
            <section className="bl-card">
              <div className="bl-card-header">
                <h2 className="bl-card-title">Allocation preview</h2>
                <span className="bl-cell-faint">
                  {report.allocation.panels.length} panel(s) for {report.allocation.domains} domain(s)
                </span>
              </div>
              <div className="bl-card-body">
                {report.allocation.by_bucket?.map((bucket) => (
                  <p key={bucket.kind}>
                    <strong>{bucket.kind === 'serken' ? 'Serken' : 'Regular'}:</strong>{' '}
                    {bucket.placed_domains} / {bucket.requested_domains} domains,{' '}
                    {bucket.mailboxes} mailboxes across {bucket.panels} panels
                    {bucket.shortfall_domains > 0 ? ` · ${bucket.shortfall_domains} domains waiting for this panel type` : ''}
                  </p>
                ))}
                <span className="bl-hint">
                  A plan, not a reservation. Launch turns it into claimed, licensed seats in the same
                  breath - which is the only thing that stops another batch taking them.
                </span>
                {report.allocation.leftovers.length > 0 && (
                  <div className="bl-notice bl-notice--warn">
                    <strong>{report.allocation.leftovers.length} domain(s) could not be placed</strong>{' '}
                    with the seats currently free. The order still launches; the coordinator re-plans
                    them every ten minutes as capacity appears.
                    <div className="bl-cell-mono">{report.allocation.leftovers.join(', ')}</div>
                  </div>
                )}
              </div>
              {report.allocation.panels.length > 0 && (
                <div className="bl-table-wrap">
                  <table className="bl-table">
                    <thead>
                      <tr>
                        <th>Panel</th>
                        <th>Type</th>
                        <th className="bl-col-center">Domains taken</th>
                        <th className="bl-col-center">Mailboxes</th>
                        <th className="bl-col-center">Seats left after</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.allocation.panels.map((row) => (
                        <tr key={row.panel_email}>
                          <td className="bl-cell-mono">{row.panel_email}</td>
                          <td>{row.kind === 'serken' ? 'Serken' : row.kind === 'regular' ? 'Regular' : '—'}</td>
                          <td className="bl-col-center bl-num">{row.domains ?? '—'}</td>
                          <td className="bl-col-center bl-num">{row.take}</td>
                          <td className="bl-col-center bl-num">{row.remaining}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          <section className="bl-card">
            <div className="bl-card-body">
              <div className="bl-notice bl-notice--warn">
                <strong>Launch is the one irreversible button here.</strong> Allocation and the first
                claims go out together, and from that moment the coordinator owns the order: seats
                are licensed, zones are written and money is spent. Aborting later tears nothing
                down - what was built stays built.
              </div>
              <div className="bl-actions">
                <button type="button" className="bl-btn bl-btn--ghost" disabled={launching} onClick={resetAll}>
                  Start over
                </button>
                <div className="bl-actions-spacer" />
                <button
                  type="button"
                  className="bl-btn bl-btn--primary"
                  disabled={launching || preflightBusy || report === null || !report.pass}
                  onClick={() => void launch()}
                >
                  {launching ? 'Launching...' : `Launch order ${draft.order.id}`}
                </button>
              </div>
              {report !== null && !report.pass && (
                <span className="bl-hint">
                  Launch stays disabled until a preflight passes: the server refuses an order without
                  a passing report taken against this exact snapshot, so fix what the gates named and
                  run it again.
                </span>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}

/* ─── Pieces ────────────────────────────────────────────────────────────── */

function Gate({
  name,
  result,
  detail,
  soft,
}: {
  name: string;
  result: { ok: boolean; problems: string[] };
  detail?: string;
  /** A gate that is reported rather than gating: it fails amber, not red. */
  soft?: boolean;
}): JSX.Element {
  const tone = result.ok ? 'ok' : soft ? 'warn' : 'bad';
  return (
    <div className={`bl-gate bl-gate--${tone}`}>
      <div className="bl-gate-head">
        <span className="bl-gate-mark">{result.ok ? 'PASS' : soft ? 'NOTE' : 'FAIL'}</span>
        <span className="bl-gate-name">{name}</span>
        {detail && <span className="bl-gate-detail">{detail}</span>}
      </div>
      {result.problems.length > 0 && (
        <ul className="bl-gate-problems">
          {result.problems.map((problem, i) => <li key={i}>{problem}</li>)}
        </ul>
      )}
    </div>
  );
}

function PersonaRow({
  persona,
  photo,
  onChange,
}: {
  persona: Persona;
  photo: PersonaPhoto | undefined;
  onChange: (next: PersonaPhoto) => void;
}): JSX.Element {
  const entry = photo ?? {};
  // Not stored on the entry: it is derived from what is in the box right now, so
  // it clears itself the moment the URL becomes acceptable.
  const urlProblem = photoUrlProblem(entry);

  async function pick(file: File | undefined): Promise<void> {
    if (!file) return;
    // Refused in the browser, while the operator still has the file open. The
    // server enforces the same cap and the station enforces it again.
    if (file.size > MAX_PHOTO_BYTES) {
      onChange({
        filename: file.name,
        bytes: file.size,
        error: `${(file.size / 1024 / 1024).toFixed(1)} MB is over the `
          + `${Math.round(MAX_PHOTO_BYTES / 1024 / 1024)} MB cap - shrink it and try again`,
      });
      return;
    }
    try {
      const { b64, mime, bytes } = await readFileAsBase64(file);
      onChange({ b64, mime, bytes, filename: file.name });
    } catch (err) {
      onChange({ filename: file.name, error: err instanceof Error ? err.message : 'the file could not be read' });
    }
  }

  return (
    <tr className={entry.error || urlProblem ? 'bl-row--bad' : undefined}>
      <td>
        <strong>{persona.first_name} {persona.last_name}</strong>
      </td>
      <td className="bl-cell-mono bl-cell-trunc" title={persona.emails.join(', ')}>
        {persona.locals.join(', ')}
      </td>
      <td className="bl-col-center bl-num">{persona.emails.length}</td>
      <td>
        <div className="bl-photocell">
          <input
            className="bl-input bl-mono"
            value={entry.b64 ? '' : entry.url ?? ''}
            disabled={Boolean(entry.b64)}
            placeholder="https://... or upload"
            onChange={(e) => onChange({ url: e.target.value })}
          />
          <input
            type="file"
            accept="image/*"
            className="bl-file"
            onChange={(e) => {
              void pick(e.target.files?.[0]);
              // Cleared so re-picking the same file fires change again.
              e.target.value = '';
            }}
          />
          {entry.b64 && (
            <span className="bl-filechip">
              {entry.filename ?? 'image'} ({Math.round((entry.bytes ?? 0) / 1024)} KB)
              <button type="button" className="bl-chip-x" onClick={() => onChange({})} aria-label="remove the image">
                x
              </button>
            </span>
          )}
          {entry.error && <span className="bl-photoerr">{entry.error}</span>}
          {urlProblem && <span className="bl-photoerr">{urlProblem}</span>}
        </div>
      </td>
    </tr>
  );
}
